import { ReactNode } from 'react';

import {
  faCheckCircle,
  faDownload,
  faInfoCircle
} from '@fortawesome/pro-light-svg-icons';
import { MuiTheme } from 'config/materialUI';
import i18n from 'i18next';
import { createRoot } from 'react-dom/client';

import { download } from 'utils/download';

import { IconButton, ThemeProvider } from '@mui/material';
import Alert, { AlertProps } from '@mui/material/Alert';
import Snackbar, { SnackbarProps } from '@mui/material/Snackbar';

import FontAwesomeSvgIcon from 'components/shared/FontAwesomeSvgIcon';

type CustomProps = {
  hideCloseButton?: true;
  messageCode?: string;
  messageCodeProps?: Record<string, string>;
  downloadUrl?: string;
  filename?: string;
  variant?: string;
  color?: string;
  icon?: ReactNode;
};

export default function showSnackbar(
  props: SnackbarProps & AlertProps & CustomProps
) {
  const {
    message,
    messageCode,
    messageCodeProps,
    downloadUrl,
    filename,
    severity,
    anchorOrigin,
    autoHideDuration = 6000,
    variant,
    color,
    icon,
    hideCloseButton
  } = props;

  const container = document.createElement('div');

  document.body.appendChild(container);

  const root = createRoot(container);

  const onClose = () => {
    renderSnackBar(false);
    root.unmount();
  };

  const getMessageCodeProps = () => {
    const translatedProps = {};
    const mcp = messageCodeProps || {};
    Object.keys(mcp).forEach((key) => {
      const value = mcp[key] || '';
      translatedProps[key] = i18n.t(value);
    });
    return translatedProps;
  };
  const getMessage = () => {
    return messageCode
      ? i18n.t(messageCode, getMessageCodeProps())
      : typeof message === 'string'
      ? message
      : JSON.stringify(message);
  };

  const renderSnackBar = (isOpen: boolean) => {
    root.render(
      <ThemeProvider theme={MuiTheme}>
        <Snackbar
          open={isOpen}
          autoHideDuration={autoHideDuration}
          anchorOrigin={
            anchorOrigin || { vertical: 'top', horizontal: 'center' }
          }
          TransitionProps={{
            onExited: () => document.body.removeChild(container)
          }}
          onClose={onClose}
        >
          <Alert
            severity={severity}
            onClose={hideCloseButton ? undefined : onClose}
            variant={variant}
            color={color}
            icon={icon}
            iconMapping={{
              success: <FontAwesomeSvgIcon icon={faCheckCircle} />,
              info: <FontAwesomeSvgIcon icon={faInfoCircle} />
            }}
            action={
              downloadUrl &&
              filename && (
                <IconButton
                  onClick={() => download(downloadUrl, filename)}
                  color="primary"
                  size="small"
                >
                  <FontAwesomeSvgIcon icon={faDownload} fontSize="small" />
                </IconButton>
              )
            }
          >
            {getMessage()}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    );
  };

  renderSnackBar(true);
}

export const showSuccessSnackbar = () =>
  showSnackbar({
    severity: 'success',
    message: i18n.t('changesSaved')
  });

export const showErrorSnackbar = (error?: string) =>
  showSnackbar({
    severity: 'error',
    message: error ?? i18n.t('somethingWentWrong')
  });
